import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Button,
  Divider,
  Autocomplete,
  CircularProgress,
  Select,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { OrgContext } from '../contexts/OrgContext';
import { getProductsByOrgId } from '../services/productService';
import { getCustomers, addCustomer } from '../services/customerService';
import { addOrder } from '../services/orderService';
import CurrencyFormatter from '../utils/CurrencyFormatter';
import CameraScanner from '../components/CameraScanner';

const QuickSalePage = () => {
  const { orgId } = useContext(OrgContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch] = useState('');
  const [quantities, setQuantities] = useState({}); // productId -> qty
  const [itemDiscounts, setItemDiscounts] = useState({}); // productId -> { type, value }
  const [cartDiscountType, setCartDiscountType] = useState('none'); // 'none' | 'percentage' | 'amount'
  const [cartDiscountValue, setCartDiscountValue] = useState('');
  const [taxPercentage, setTaxPercentage] = useState(''); // order-level tax %
  const [cartExpanded, setCartExpanded] = useState(true);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [submitting, setSubmitting] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!orgId) return;
      setLoading(true);
      try {
        const [prodRes, custRes] = await Promise.all([
          getProductsByOrgId(orgId, { limit: 0, page: 1 }),
          getCustomers(orgId, { limit: 50, page: 1 }),
        ]);
        const arr = (r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []);
        if (mounted) {
          setProducts(arr(prodRes));
          setCustomers(arr(custRes));
        }
      } catch (e) {
        console.warn('QuickSale load failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [orgId]);

  const filtered = useMemo(() => {
    if (!search) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        (p.product_name || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.sub_category || '').toLowerCase().includes(q),
    );
  }, [products, search]);

  const categories = useMemo(() => {
    const map = new Map();
    for (const p of filtered) {
      const key = p.category || 'Uncategorized';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const findProductByCode = React.useCallback((code) => {
    if (!code) return null;
    const upper = code.toString().trim().toUpperCase();
    return products.find((p) => {
      const refs = [p.scanner_reference, p.sku]
        .filter(Boolean)
        .map((v) => v.toString().trim().toUpperCase());
      return refs.includes(upper);
    }) || null;
  }, [products]);

  const addProduct = (p) => {
    setQuantities((prev) => ({ ...prev, [p.id]: (prev[p.id] || 0) + 1 }));
    setItemDiscounts((prev) => ({
      ...prev,
      [p.id]: prev[p.id] || { type: 'none', value: '' },
    }));
  };

  const removeProduct = (productId) => {
    let removedCompletely = false;
    setQuantities((prev) => {
      const qty = prev[productId] || 0;
      if (!qty) return prev;
      const next = { ...prev };
      if (qty > 1) {
        next[productId] = qty - 1;
      } else {
        delete next[productId];
        removedCompletely = true;
      }
      return next;
    });
    if (removedCompletely) {
      setItemDiscounts((prev) => {
        if (!(productId in prev)) return prev;
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const setQty = (id, qty) => {
    const q = Math.max(0, parseInt(qty || '0', 10));
    setQuantities((prev) => ({ ...prev, [id]: q }));
  };

  const setItemDiscount = (id, field, value) => {
    setItemDiscounts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { type: 'none', value: '' }), [field]: value },
    }));
  };

  const computeItemUnitAfterDiscount = (original, type, valueRaw) => {
    const value = parseFloat(valueRaw || '');
    if (!type || type === 'none' || isNaN(value)) return Math.max(0, Number(original || 0));
    const base = Number(original || 0);
    if (type === 'percentage') return Math.max(0, base * (1 - value / 100));
    if (type === 'amount') return Math.max(0, base - value);
    return Math.max(0, base);
  };

  const items = useMemo(() => {
    const out = [];
    for (const p of products) {
      const q = quantities[p.id];
      if (q > 0) {
        const originalUnit = Number(p.current_unit_price || p.unit_price || 0);
        const d = itemDiscounts[p.id] || { type: 'none', value: '' };
        const unit = computeItemUnitAfterDiscount(originalUnit, d.type, d.value);
        out.push({
          id: p.id,
          name: p.product_name,
          original_unit_price: originalUnit,
          unit_price: unit,
          qty: q,
          total: unit * q,
          unit_of_measure: p.unit_of_measure,
          discount_type: d.type || 'none',
          discount_value: d.value || '',
        });
      }
    }
    return out;
  }, [products, quantities, itemDiscounts]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + it.total, 0);
    let cartDiscount = 0;
    const cdv = parseFloat(cartDiscountValue || '');
    if (cartDiscountType === 'percentage' && !isNaN(cdv)) {
      cartDiscount = subtotal * (cdv / 100);
    } else if (cartDiscountType === 'amount' && !isNaN(cdv)) {
      cartDiscount = Math.min(subtotal, Math.max(0, cdv));
    }
    const afterCart = Math.max(0, subtotal - cartDiscount);
    const tp = parseFloat(taxPercentage || '');
    const taxAmount = !isNaN(tp) ? afterCart * (tp / 100) : 0;
    const total = afterCart + taxAmount;
    return { subtotal, cartDiscount, afterCart, taxAmount, total };
  }, [items, cartDiscountType, cartDiscountValue, taxPercentage]);

  const ensureAnonymousCustomer = async () => {
    // Try to find existing anonymous else create
    const name = 'Anonymous Customer';
    const phone = '9999999999';
    try {
      const all = await getCustomers(orgId, { limit: 0, page: 1 });
      const arr = (r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []);
      const list = arr(all);
      const found = list.find(
        (c) =>
          (c.customer_name || '').toLowerCase() === name.toLowerCase() ||
          (c.contact_number || '') === phone,
      );
      if (found) return found;
    } catch {}
    // Create via API
    const created = await addCustomer(orgId, {
      customer_name: name,
      contact_number: '9999999999',
    });
    return created;
  };

  const handleCompleteOrder = async () => {
    if (!items.length || !orgId) return;
    setSubmitting(true);
    try {
      let customer = selectedCustomer;
      if (!customer) {
        customer = await ensureAnonymousCustomer();
      }
      // Apply cart-level discount by proportionally adjusting unit prices
      const subtotal = items.reduce((s, it) => s + it.unit_price * it.qty, 0);
      let cartPct = 0;
      let cartAmt = 0;
      const cdv = parseFloat(cartDiscountValue || '');
      if (cartDiscountType === 'percentage' && !isNaN(cdv)) cartPct = Math.max(0, cdv);
      if (cartDiscountType === 'amount' && !isNaN(cdv)) cartAmt = Math.min(subtotal, Math.max(0, cdv));

      const adjusted = items.map((it) => {
        let unit = it.unit_price;
        if (cartPct > 0) unit = unit * (1 - cartPct / 100);
        else if (cartAmt > 0 && subtotal > 0) {
          const share = (it.unit_price * it.qty) / subtotal;
          const cutPerItem = (cartAmt * share) / it.qty;
          unit = Math.max(0, unit - cutPerItem);
        }
        const total = unit * it.qty;
        return {
          product_id: it.id,
          quantity: it.qty,
          unit_price: Number(unit.toFixed(2)),
          total_price: Number(total.toFixed(2)),
          original_unit_price: Number(it.original_unit_price?.toFixed ? it.original_unit_price.toFixed(2) : it.original_unit_price) || it.unit_price,
          discount_type: it.discount_type || 'none',
          discount_value: it.discount_value ? Number(it.discount_value) : 0,
        };
      });

      const tp = parseFloat(taxPercentage || '');
      const taxPct = !isNaN(tp) ? tp : 0;
      const line_items = adjusted;
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        org_id: Number(orgId),
        order_type: 'Sale',
        order_status: 'Completed',
        order_date: today,
        delivery_date: today,
        customer_id: customer.id,
        line_items,
        amount: Number(totals.total.toFixed(2)),
        meta: {
          tax_percentage: taxPct,
          tax_amount: Number(totals.taxAmount.toFixed(2)),
          subtotal: Number(totals.subtotal.toFixed(2)),
          cart_discount: { type: cartDiscountType, value: cartDiscountValue || null },
          quick_sale: true,
        },
        payment_type: 'cash',
        payment_due_date: null,
      };
      const res = await addOrder(payload);
      // Navigate to order detail
      navigate(`/${orgId}/orders/${res.id}`);
    } catch (e) {
      console.error('Quick sale failed', e);
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Quick Sale
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search products"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
        size="small"
        fullWidth
        sx={{ flexGrow: 1, minWidth: 320, maxWidth: 720 }}
            />
            <Autocomplete
              options={customers}
              value={selectedCustomer}
              onChange={(_, v) => setSelectedCustomer(v)}
              getOptionLabel={(o) => (o ? `${o.customer_name} (ID: ${o.id})` : '')}
              sx={{ minWidth: 260 }}
              renderInput={(params) => <TextField {...params} label="Customer (optional)" size="small" />}
            />
            <Button variant="outlined" onClick={() => setScanOpen(true)}>
              Scan via Camera
            </Button>
          </Box>

          {/* Category sections */}
          <Box>
            {categories.map(([cat, prods]) => (
              <Accordion key={cat} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{cat}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {prods.map((p) => {
                      const unit = Number(p.current_unit_price || p.unit_price || 0);
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                          <Card variant="outlined">
                            <CardActionArea onClick={() => addProduct(p)}>
                              <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                  {p.product_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {p.sku || ''}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                  <CurrencyFormatter value={unit} />
                                </Box>
                                {quantities[p.id] ? (
                                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    In cart: {quantities[p.id]}
                                  </Typography>
                                ) : null}
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Sticky Cart */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 -6px 18px rgba(0,0,0,0.35)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Cart • <CurrencyFormatter value={totals.total} />
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={!items.length || submitting}
                  onClick={handleCompleteOrder}
                >
                  {submitting ? 'Submitting…' : 'Complete Order'}
                </Button>
                <Button size="small" onClick={() => setCartExpanded((e) => !e)}>
                  {cartExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                </Button>
              </Box>
            </Box>
            {cartExpanded && (
              <Box
                sx={{
                  px: 2,
                  pb: 2,
                  maxHeight: isMobile ? '20vh' : '15vh',
                  overflowY: 'auto',
                }}
              >
                {items.length === 0 ? (
                  <Typography color="text.secondary">No items added yet. Click product cards to add.</Typography>
                ) : (
                  <>
                    {items.map((it) => (
                      <Box key={it.id} sx={{ display: 'grid', gridTemplateColumns: '1.2fr 0.6fr 0.8fr 0.8fr 0.6fr', gap: 1, py: 0.75, borderBottom: '1px dashed', borderColor: 'divider', alignItems: 'center' }}>
                        <Box sx={{ minWidth: 160 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {it.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Unit: <CurrencyFormatter value={it.unit_price} /> (orig <CurrencyFormatter value={it.original_unit_price} />)
                          </Typography>
                        </Box>
                        <TextField
                          size="small"
                          type="number"
                          label="Qty"
                          value={quantities[it.id] || 0}
                          onChange={(e) => setQty(it.id, e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                        <Select
                          size="small"
                          value={(itemDiscounts[it.id]?.type) || 'none'}
                          onChange={(e) => setItemDiscount(it.id, 'type', e.target.value)}
                        >
                          <MenuItem value="none">No Discount</MenuItem>
                          <MenuItem value="percentage">% Off</MenuItem>
                          <MenuItem value="amount">Amount Off</MenuItem>
                        </Select>
                        <TextField
                          size="small"
                          type="number"
                          label="Discount"
                          value={(itemDiscounts[it.id]?.value) || ''}
                          onChange={(e) => setItemDiscount(it.id, 'value', e.target.value)}
                          disabled={(itemDiscounts[it.id]?.type || 'none') === 'none'}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
                          <CurrencyFormatter value={it.total} />
                        </Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.8fr 0.8fr 0.6fr', gap: 1, py: 1, alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Cart Discount</Typography>
                      <Box />
                      <Select size="small" value={cartDiscountType} onChange={(e) => setCartDiscountType(e.target.value)}>
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="percentage">% Off</MenuItem>
                        <MenuItem value="amount">Amount Off</MenuItem>
                      </Select>
                      <TextField size="small" type="number" label="Value" value={cartDiscountValue} onChange={(e) => setCartDiscountValue(e.target.value)} disabled={cartDiscountType === 'none'} />
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>−<CurrencyFormatter value={totals.cartDiscount} /></Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.8fr 0.8fr 0.6fr', gap: 1, py: 0.5, alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Tax %</Typography>
                      <Box />
                      <Box />
                      <TextField size="small" type="number" label="Tax %" value={taxPercentage} onChange={(e) => setTaxPercentage(e.target.value)} />
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>+<CurrencyFormatter value={totals.taxAmount} /></Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><CurrencyFormatter value={totals.total} /></Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </>
      )}
      <CameraScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onDetected={(code) => {
          const p = findProductByCode(code);
          if (p) addProduct(p);
        }}
        continuous
        title="Scan Products"
  scannedItems={items.map((it) => ({ id: it.id, name: it.name, qty: quantities[it.id] || it.qty || 1 }))}
  onRemoveItem={(id) => removeProduct(id)}
      />
    </Paper>
  );
};

export default QuickSalePage;
